import React from 'react';
import { connect } from 'react-redux';

import PROMISE_STATUS from 'constants/promiseStatus.const';
import useAef from 'hooks/useAef.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { selectCheckoutDataCods, selectCheckoutDepositListSuccess, selectCheckoutDepositUnlock, selectCheckoutInitialDeposit, selectCheckoutIsFetching } from 'selectors/checkout.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import fromArrayToCommatose from 'utils/converters/string/fromArrayToCommatose.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import allSettled from 'utils/functions/allSettled.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import { fetchClientDepositDetails, getAppointmentStartTimeList } from 'booking/actions';
import { fetchAllDepositClientsList, fetchClientDepositList, setInitalDepositSelection, unlockDeposit } from 'checkout/actions';
import ClientsDepositTable from 'checkout/components/ClientsDepositTable';

const CTAG = 'ClientsDepositTableLoader';
const L = logger(CTAG);

// NOTE: this component is created as a workaround to solve the original ClientsDepositTable data access/mutation issues

const unignoreAll = (tag, ...args) => {
  const promises = args.pop();

  for (const p of asArray(promises)) {
    if (p instanceof Promise && p.status === PROMISE_STATUS.rejected) {
      p.catch((reason) => {
        // eslint-disable-next-line no-console
        console.warn('[W]', `[${tag}]`, ...args, reason);
      });
    }
  }

  return promises;
};

const ClientsDepositTableLoader = (props) => {
  const { salonId, setInitalDepositSelection, appointments: appointmentIdsFromProps, fetchClientDepositList, clients: clientsFromProps, clientId } = props;

  const [prevClientsCount, setPrevClientsCount] = useStateWhileMounted(0);
  const [prevAppointmentsCount, setPrevAppointmentsCount] = useStateWhileMounted(0);

  const { isLoaded } = useAef(async () => {
    const appointmentIds = asArray(appointmentIdsFromProps);
    const clients = asArray(clientsFromProps);

    const nextClientsCount = clients.length;
    const nextAppointmentsCount = appointmentIds.length;

    const hasItems = nextAppointmentsCount || nextClientsCount;
    const hasSameClients = prevClientsCount === nextClientsCount;
    const hasNewAppointment = prevAppointmentsCount < nextAppointmentsCount;

    if (hasItems && (hasNewAppointment || !hasSameClients)) {
      const aIds = fromArrayToCommatose(appointmentIds);

      const promises = clients.map((c) => fetchClientDepositList(salonId, fromAnyToNumber(asObject(c).id || clientId) || 0, aIds));

      try {
        // use try-catch here as to be able to continue with the code after it, even if there is a rejection
        await allSettled(promises);
      } catch (e) {
        if (e instanceof TypeError) {
          // in case the promises aren't of the expected type, print it
          // unignoreAll is supposed to print out the other warnings, for each promise
          L.warn('useAef()', e);
        }
        unignoreAll('useAef()', 'fetchClientDepositList:', promises);
      } // try-catch
    }

    setPrevClientsCount(nextClientsCount);
    setPrevAppointmentsCount(nextAppointmentsCount);

    for (const appointmentId of appointmentIds) {
      L.debug('useAef()', 'setInitalDepositSelection for salonId:', salonId, 'and appointmentId:', appointmentId);
      setInitalDepositSelection(salonId, appointmentId);
    }
  }, [
    appointmentIdsFromProps,
    clientsFromProps,
    prevClientsCount,
    prevAppointmentsCount,
    setPrevClientsCount,
    setPrevAppointmentsCount,
    fetchClientDepositList,
    salonId,
    clientId,
    setInitalDepositSelection,
  ]);

  // NOTE: one can use depositClientListSuccess for loading indicator

  return <ClientsDepositTable {...props} isLoaded={isLoaded} />;
};

const ClientsDepositTableLoaderHoc = connect(
  (state) => ({
    isFetching: selectCheckoutIsFetching(state),
    initialDeposit: selectCheckoutInitialDeposit(state),
    clientsOutstandingDeposits: selectCheckoutDataCods(state) || {},
    depositClientListSuccess: selectCheckoutDepositListSuccess(state),
    depositUnlock: selectCheckoutDepositUnlock(state),
  }),
  mapActionsToDispatchToProps({
    setInitalDepositSelection,
    fetchClientDepositList,
    fetchAllDepositClientsList,
    getAppointmentStartTimeList,
    fetchClientDepositDetails,
    unlockDeposit,
  }),
)(ClientsDepositTableLoader);

// noinspection JSUnusedGlobalSymbols
export default ClientsDepositTableLoaderHoc;
